import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AcceptingStatus,
  Availability,
  PracticeGroupSummary,
} from "@/lib/availability";
import type { GpReported } from "@/lib/wait-reports";

/**
 * Unauthenticated directory hook — pulls real Canberra facilities from
 * GET /v1/public/:citySlug/directory. NO HALLUCINATED DATA. Every row
 * returned carries its source provenance in `address_json.source`.
 */

export interface FacilityAddressJson {
  street?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  bulk_billing?: "yes" | "no" | "mixed" | "full" | "private" | null;
  source?: string;
  source_date?: string;
  email?: string | null;
  fax?: string | null;
  // specialist + allied health fields
  category?: string | null;    // Google Places category (Apify data)
  full_address?: string | null;
  // specialist-specific fields stored in address_json
  title?: string | null;
  specialty?: string | null;
  sub_specialties?: string[];
  practice_name?: string | null;
  hours?: string | null;
  phone_alt?: string | null;
  also_consults_at?: string | null;
  consulting_locations?: string | null;
}

export interface Facility {
  id: string;
  memberUserId?: string | null;
  slug: string;
  name: string;
  facility_type: "gp_practice" | "specialist" | "allied_health" | string;
  phone: string | null;
  website: string | null;
  photo_url: string | null;
  address_json: FacilityAddressJson;
  availability: Availability | null;
  practiceGroup: PracticeGroupSummary | null;
  gpReported: GpReported | null;
}

export interface DirectoryMeta {
  total: number;
  gpCount: number;
  specialistCount: number;
  alliedCount: number;
  suburbCount: number;
  limit: number;
  offset: number;
}

export interface DirectoryResponse {
  city: { slug: string; name: string };
  items: Facility[];
  meta: DirectoryMeta;
}

export interface DirectoryFilters {
  type?: "gp_practice" | "specialist" | "allied_health";
  suburb?: string;
  bulkBill?: string;
  accepting?: Extract<AcceptingStatus, "yes" | "urgent_only">;
  specialty?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface PublicSpecialty {
  slug: string;
  name: string;
  count: number;
  acceptingCount: number;
  tileCount: number;
}

export function usePublicSpecialties(citySlug: string) {
  return useQuery({
    queryKey: ["public-specialties", citySlug],
    queryFn: () =>
      api.get<PublicSpecialty[]>(`/v1/public/${citySlug}/specialties`, {
        skipAuth: true,
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: Boolean(citySlug),
  });
}

export function usePublicDirectory(citySlug: string, filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: ["public-directory", citySlug, filters],
    queryFn: () =>
      api.get<DirectoryResponse>(`/v1/public/${citySlug}/directory`, {
        skipAuth: true,
        params: filters as Record<string, string | number | undefined>,
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export interface SpecialistProfileResponse {
  city: { slug: string; name: string };
  specialist: Facility;
  tiles: Array<{ id: string; title: string; publishedAt: string }>;
}

export function useSpecialistProfile(citySlug: string, slug: string) {
  return useQuery({
    queryKey: ["specialist-profile", citySlug, slug],
    queryFn: () =>
      api.get<SpecialistProfileResponse>(
        `/v1/public/${citySlug}/directory/specialist/${slug}`,
        { skipAuth: true },
      ),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: Boolean(citySlug && slug),
  });
}

export interface CorrectionPayload {
  reason: string;
  note?: string;
  reporterEmail?: string;
  reporterName?: string;
}

export function submitCorrection(citySlug: string, slug: string, payload: CorrectionPayload) {
  return api.post(
    `/v1/public/${citySlug}/directory/specialist/${slug}/correction`,
    payload,
    { skipAuth: true },
  );
}

/** Friendly bulk-billing label for cards. */
export function bulkBillingLabel(v: string | null | undefined): string | null {
  switch (v) {
    case "yes":
    case "full":
      return "Bulk-bills";
    case "mixed":
      return "Mixed billing";
    case "no":
    case "private":
      return "Private billing";
    default:
      return null;
  }
}
